import { Check, ChevronRight } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Shortcut } from './kbd';
import { menuItem, menuSurface } from './menu-styles';

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;
export const MenuGroup = DropdownMenu.Group;
export const MenuSub = DropdownMenu.Sub;

const surface = menuSurface;
const itemBase = menuItem;

export function MenuContent({
  className,
  align = 'end',
  sideOffset = 4,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn(surface, className)}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}

export interface MenuItemProps extends ComponentPropsWithRef<typeof DropdownMenu.Item> {
  icon?: ReactNode;
  shortcut?: readonly string[];
}

export function MenuItem({ icon, shortcut, children, className, ...props }: MenuItemProps) {
  return (
    <DropdownMenu.Item className={cn(itemBase, className)} {...props}>
      {icon}
      <span className="flex-1 truncate">{children}</span>
      {shortcut && <Shortcut keys={shortcut} />}
    </DropdownMenu.Item>
  );
}

export function MenuLabel({
  className,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.Label>) {
  return (
    <DropdownMenu.Label
      className={cn('px-2 pt-1.5 pb-1 text-xs font-medium text-text-3', className)}
      {...props}
    />
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="-mx-1 my-1 h-px bg-border-subtle" />;
}

export const MenuRadioGroup = DropdownMenu.RadioGroup;

export function MenuRadioItem({
  children,
  className,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.RadioItem>) {
  return (
    <DropdownMenu.RadioItem className={cn(itemBase, 'pr-7', className)} {...props}>
      {children}
      <DropdownMenu.ItemIndicator className="absolute right-2 inline-flex">
        <Check className="!text-text-1" />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.RadioItem>
  );
}

export function MenuCheckboxItem({
  children,
  className,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.CheckboxItem>) {
  return (
    <DropdownMenu.CheckboxItem className={cn(itemBase, 'pr-7', className)} {...props}>
      {children}
      <DropdownMenu.ItemIndicator className="absolute right-2 inline-flex">
        <Check className="!text-text-1" />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.CheckboxItem>
  );
}

export function MenuSubTrigger({
  icon,
  children,
  className,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.SubTrigger> & { icon?: ReactNode }) {
  return (
    <DropdownMenu.SubTrigger className={cn(itemBase, className)} {...props}>
      {icon}
      <span className="flex-1 truncate">{children}</span>
      <ChevronRight className="-mr-1" />
    </DropdownMenu.SubTrigger>
  );
}

export function MenuSubContent({
  className,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.SubContent>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.SubContent
        sideOffset={6}
        collisionPadding={8}
        className={cn(surface, 'min-w-40', className)}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}
